import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { ApiPostRequest, ApiPostResponse } from "@AxiosWrapper";
import type { FormTemplateField as FormTemplateSection } from "@ServerTypes/forms/forms.schema";
import type { FileNomenclature } from "@ServerTypes/folders/folders.schema";

export interface FileUploadItem {
  /** The browser File object being uploaded. */
  file?: File | undefined;
  /** Error message from the Excel-import validation flow. */
  excelImportError?: string | undefined;
  isDeleted?: boolean | undefined;
  isFolder?: boolean | undefined;
  totalBytesUploaded?: number | undefined;
  size?: number | undefined;
  progress?: number | undefined;
  bytesUploaded?: number | undefined;
  location?: string[] | undefined;
  documentType?: string[] | undefined;
  isMetadataPending?: boolean | undefined;
  name?: string | undefined;
  type?: string | undefined;
  fileId?: string | undefined;
  folderId?: string | undefined;
  parentId?: string | undefined;
  spaceId?: string | undefined;
  hierarchyId?: string | undefined;
  status?: string | undefined;
  uploadId?: string | undefined;
  key?: string | undefined;
  url?: string | undefined;
  error?: string | null | undefined;
  errors?: string[] | undefined;
  errorMessage?: string | undefined;
  progressCompleted?: boolean | undefined;
  progressStarted?: boolean | undefined;
  duplicateFileStatus?:
    | {
        found?: boolean | undefined;
        newSuggestedName?: string | undefined;
        isDuplicateContainer?: boolean | undefined;
        noMatchingContainer?: boolean | undefined;
      }
    | null
    | undefined;
  isDuplicate?: boolean | undefined;
  customFieldsAutoFilled?: boolean | undefined;
  uploadLocation?: string | null | undefined;
  fileVersionId?: string | undefined;
  originalName?: string | undefined;
  extension?: string | undefined;
  mimeType?: string | undefined;
  fileNomenclature?: FileNomenclature | null | undefined;
  isUploadAllowed?: boolean | undefined;
  formTemplateId?: string | undefined;
  folderPath?: string | undefined;
  batchId?: string | undefined;
  isFileVersion?: boolean | undefined;
  versionId?: string | undefined;
  uploadStartTime?: number | undefined;
  uploadEndTime?: number | undefined;
  isMetadataFilled?: boolean | undefined;
  currentFolderId?: string | undefined;
  controller?: AbortController | AbortController[] | null | undefined;
  triggerFileUpload?: boolean | undefined;
  id?: number | string | undefined;
  createdDate?: number | undefined;
  uploaded?: boolean | undefined;
  processing?: boolean | undefined;
  processed?: boolean | undefined;
  uploadTriggered?: boolean | undefined;
  /** Set once the post-upload custom-field/nomenclature follow-up has run for this row. */
  uploadCompleteProcessed?: boolean | undefined;
  revisionRejected?: boolean | undefined;
  nomenclature?: string[] | undefined;
  isFileNomenclatureCorrect?: boolean | undefined;
  nomenclatureUpdated?: boolean | undefined;
  customFieldsUpdated?: boolean | undefined;
  customFieldsSaved?: boolean | undefined;
  isBulkEdit?: boolean | undefined;
  folder?: { fileNomenclature?: FileNomenclature | null | undefined; [key: string]: unknown } | undefined;
  matchedContainer?: { name?: string | undefined; errors?: string[] | undefined; [key: string]: unknown } | undefined;
  matchedDirectImportContainer?:
    | {
        containerData?: { errors?: string[] | undefined; [key: string]: unknown } | undefined;
        [key: string]: unknown;
      }
    | undefined;
}

interface UpdateFileUploadPayload {
  index: number;
  data: Partial<FileUploadItem>;
  spaceId?: string | undefined;
  hierarchyId?: string | undefined;
}

interface DocumentChangePayload {
  index: number;
  data: string;
}

export interface FileUploadFormTemplate {
  /** The captured form-template SECTIONS (each carrying `questionFields`), not a flat field list. */
  template: FormTemplateSection[];
  formTemplateId?: string | undefined;
  name?: string | undefined;
  typeId?: string | undefined;
}

export interface FileUploadKeyData {
  fileUploadList?: FileUploadItem[] | undefined;
  spaceId?: string | undefined;
  hierarchyId?: string | undefined;
  projectId?: string | undefined;
  workspaceId?: string | undefined;
  currentFiles?: FileUploadItem[] | undefined;
  fileNomenclature?: FileNomenclature | null | undefined;
  isUploadAllowed?: boolean | undefined;
  formTemplate?: FileUploadFormTemplate | undefined;
  isFileVersionUploading?: boolean | undefined;
  uploadFolderData?: { [k: string]: string | number | boolean | null | string[] } | undefined;
  uploadSpaceId?: string | undefined;
  currentFolder?:
    | {
        _id?: string | undefined;
        name?: string | undefined;
        hierarchyId?: string | undefined;
        spaceId?: string | undefined;
        filterPath?: string | undefined;
      }
    | undefined;
}

type FileUploadPropertiesState = Record<string, FileUploadKeyData>;

interface UpdateFileUploadPropertiesPayload {
  spaceId: string;
  hierarchyId: string;
  index: number;
  data: Partial<FileUploadItem>;
}

type FileUploadState = "CLOSED" | "MINIMISED" | "MAXIMISED";

/**
 * One Excel-import container as `ExcelImport.openUploadModal` stores it; `processFileList` matches each
 * picked file on `name` + `fileFormat` + `folderPath`.
 */
export interface ExcelImportContainer {
  name?: string | undefined;
  /** The sheet's File Format cell, unconverted (the same cell as `DirectImportContainerEntry.fileFormat`). */
  fileFormat?: unknown;
  folderPath?: string | undefined;
  hierarchyId?: string | undefined;
  spaceId?: string | undefined;
  fileId?: string | undefined;
  fileVersionId?: string | undefined;
}

/**
 * One direct-import container as `ExcelImport` stores it: a validated sheet row reduced to the name,
 * format and folder an uploaded file is matched on, with the row itself kept as `containerData` — the
 * record `/fileUpload/importExcelContainers` creates the container from when no file matches it.
 */
export interface DirectImportContainerEntry {
  name: string;
  /** The row's File Format cell, unconverted. */
  fileFormat: unknown;
  folderPath: string;
  spaceId?: string | undefined;
  containerData: ApiPostRequest<"/fileUpload/importExcelContainers">["data"][number];
  revisionLabel: string;
  revisionSeriesId: unknown;
}

/**
 * The `currentData` each direct-import `/fileUpload/importExcelContainers` request is built from. Partial
 * because `ExcelImport` spreads a possibly-absent folder into it; `FileUpload` sets `spaceId` and
 * `hierarchyId` on every request.
 */
export type DirectImportCurrentData = Partial<ApiPostRequest<"/fileUpload/importExcelContainers">["currentData"]>;

// Current file upload context properties
export interface CurrentFileUploadPropertiesState {
  formTemplate?: FileUploadFormTemplate | undefined;
  currentFolderRef?:
    | {
        _id?: string | undefined;
        name?: string | undefined;
        hierarchyId?: string | undefined;
        spaceId?: string | undefined;
        filterPath?: string | undefined;
      }
    | undefined;
  isUploadAllowed?: boolean | undefined;
  fileNomenclature?: FileNomenclature | null | undefined;
  workspaceId?: string | undefined;
  hierarchyId?: string | undefined;
  fileAllowedExtensions?: { [k: string]: boolean | number | string } | undefined;
  currentFiles?: FileUploadItem[] | undefined;
  draggedFiles?: File[] | undefined;
  isFolderUpload?: boolean | undefined;
  fileUploadStateRef?: string | { current: string } | undefined;
  isExcelImportFlow?: boolean | undefined;
  excelImportContainers?: ExcelImportContainer[] | undefined;
  isDirectImportFlow?: boolean | undefined;
  directImportContainers?: DirectImportContainerEntry[] | undefined;
  directImportCurrentData?: DirectImportCurrentData | undefined;
  /** The existing entries of each direct-import target folder, keyed by folder path. */
  directImportFolderFilesMap?:
    { [k: string]: NonNullable<ApiPostResponse<"/fileSystemServer/readFiles">["files"]> } | undefined;
  formTemplateReceived?: boolean | undefined;
}

const intialFileUploadList: FileUploadItem[] = [];

const fileUploadListSlice = createSlice({
  name: "fileUploadList",
  initialState: intialFileUploadList,
  reducers: {
    // The payload is the raw upload-queue rows the FileManager builds, which carry more keys than
    // `FileUploadItem` declares; the existing assertion below is what pins them to the slice type.
    setFileUploadData: (state: FileUploadItem[], action: PayloadAction<object[]>) => {
      state.push(...(action.payload as FileUploadItem[]));
    },
    removeFileUploadData: (state: FileUploadItem[], action: PayloadAction<number>) => {
      const index = action.payload;
      if (state[index]) {
        state[index].isDeleted = true;
      }
    },
    removeAllFileUploadData: (state: FileUploadItem[]) => {
      state.forEach((item: FileUploadItem) => {
        item.isDeleted = true;
      });
    },
    updateFileUploadData: (state: FileUploadItem[], action: PayloadAction<UpdateFileUploadPayload>) => {
      const { index, data } = action.payload;
      if (!state[index]) return;
      if (state[index].isFolder && data.bytesUploaded) {
        const totalBytesUploaded = (state[index].totalBytesUploaded ?? 0) + data.bytesUploaded;
        state[index].totalBytesUploaded = totalBytesUploaded;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- DECISION NEEDED: give folder rows their own FileUploadItem arm with a required size (every folder producer sets it; ~80 references in 8 files)
        const percentageComplete = Math.floor((totalBytesUploaded * 100) / state[index].size!);
        state[index].progress = percentageComplete;
      }
      state[index] = { ...state[index], ...data };
    },
    documentChange: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index];
      // A stale index (the list was emptied or replaced meanwhile) has no row to edit: skipped, as
      // updateFileUploadData and removeFileUploadData skip it.
      if (!item) return;
      const location = item.location;
      let documentType = [data];
      if (Array.isArray(item.documentType)) {
        documentType = [...item.documentType, data];
      }
      const isMetadataPending =
        !location ||
        (Array.isArray(location) && !location.length) ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, documentType, isMetadataPending };
    },
    documentRemove: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index];
      // Stale index: no row to edit (see documentChange).
      if (!item) return;
      const location = item.location;
      const documentType = (item.documentType ?? []).filter((d: string) => d !== data);
      const isMetadataPending =
        !location ||
        (Array.isArray(location) && !location.length) ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, documentType, isMetadataPending };
    },
    locationChange: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index];
      // Stale index: no row to edit (see documentChange).
      if (!item) return;
      const documentType = item.documentType;
      let location = [data];
      if (Array.isArray(item.location)) {
        location = [...item.location, data];
      }
      const isMetadataPending =
        (Array.isArray(location) && !location.length) ||
        !documentType ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, location, isMetadataPending };
    },
    locationRemove: (state: FileUploadItem[], action: PayloadAction<DocumentChangePayload>) => {
      const { index, data } = action.payload;
      const item = state[index];
      // Stale index: no row to edit (see documentChange).
      if (!item) return;
      const location = (item.location ?? []).filter((d: string) => d !== data);
      const documentType = item.documentType;
      const isMetadataPending =
        (Array.isArray(location) && !location.length) ||
        !documentType ||
        (Array.isArray(documentType) && !documentType.length);
      state[index] = { ...item, location, isMetadataPending };
    },
    emptyFileUploadData: () => intialFileUploadList,
  },
});

const fileUploadPropertiesSlice = createSlice({
  name: "fileUploadProperties",
  initialState: {} as FileUploadPropertiesState,
  reducers: {
    setFileUploadProperties: (state: FileUploadPropertiesState, action: PayloadAction<FileUploadPropertiesState>) => {
      Object.assign(state, action.payload);
    },
    updateFileUploadData: (
      state: FileUploadPropertiesState,
      action: PayloadAction<UpdateFileUploadPropertiesPayload>,
    ) => {
      const data = action.payload;
      const key = `${data.spaceId}_${data.hierarchyId}`;
      const entry = state[key];
      if (data.spaceId && data.hierarchyId && entry) {
        const { fileUploadList = [] } = entry;
        const index = data.index;
        const list = [...fileUploadList];
        if (list[index] && list[index].isFolder && data.data.bytesUploaded) {
          const totalBytesUploaded = (list[index].totalBytesUploaded ?? 0) + data.data.bytesUploaded;
          list[index].totalBytesUploaded = totalBytesUploaded;
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- DECISION NEEDED: give folder rows their own FileUploadItem arm with a required size (every folder producer sets it; ~80 references in 8 files)
          const percentageComplete = Math.floor((totalBytesUploaded * 100) / list[index].size!);
          list[index].progress = percentageComplete;
        }
        list[index] = { ...list[index], ...data.data };
        state[key] = {
          ...entry,
          fileUploadList: list,
        };
      }
    },
    emptyFileUploadData: () => ({}),
  },
});

const currentFileUploadPropertiesSlice = createSlice({
  name: "currentFileUploadProperties",
  initialState: {} as CurrentFileUploadPropertiesState,
  reducers: {
    setCurrentFileUploadProperties: (
      _state: CurrentFileUploadPropertiesState,
      action: PayloadAction<CurrentFileUploadPropertiesState>,
    ) => action.payload,
  },
});

const fileUploadStateSlice = createSlice({
  name: "fileUploadState",
  initialState: "CLOSED" as FileUploadState,
  reducers: {
    minimiseFileUpload: () => "MINIMISED" as FileUploadState,
    maximiseFileUpload: () => "MAXIMISED" as FileUploadState,
    closeFileUpload: () => "CLOSED" as FileUploadState,
  },
});

const isFileVersionUploadingSlice = createSlice({
  name: "isFileVersionUploading",
  initialState: false,
  reducers: {
    setIsFileVersionUploading: (_state: boolean, action: PayloadAction<boolean>) => action.payload,
  },
});

const fileVersionUploadStateSlice = createSlice({
  name: "fileVersionUploadState",
  initialState: "CLOSED" as FileUploadState,
  reducers: {
    minimiseFileVersionUpload: () => "MINIMISED" as FileUploadState,
    maximiseFileVersionUpload: () => "MAXIMISED" as FileUploadState,
    closeFileVersionUpload: () => "CLOSED" as FileUploadState,
  },
});

export const {
  setFileUploadData,
  removeFileUploadData,
  removeAllFileUploadData,
  updateFileUploadData,
  documentChange,
  documentRemove,
  locationChange,
  locationRemove,
  emptyFileUploadData,
} = fileUploadListSlice.actions;

export const {
  setFileUploadProperties,
  updateFileUploadData: updateFileUploadProperties,
  emptyFileUploadData: emptyFileUploadProperties,
} = fileUploadPropertiesSlice.actions;

export const { setCurrentFileUploadProperties } = currentFileUploadPropertiesSlice.actions;

export const { minimiseFileUpload, maximiseFileUpload, closeFileUpload } = fileUploadStateSlice.actions;

export const { setIsFileVersionUploading } = isFileVersionUploadingSlice.actions;

export const { minimiseFileVersionUpload, maximiseFileVersionUpload, closeFileVersionUpload } =
  fileVersionUploadStateSlice.actions;

export const fileUploadList = fileUploadListSlice.reducer;
export const fileUploadProperties = fileUploadPropertiesSlice.reducer;
export const currentFileUploadProperties = currentFileUploadPropertiesSlice.reducer;
export const fileUploadState = fileUploadStateSlice.reducer;
export const isFileVersionUploading = isFileVersionUploadingSlice.reducer;
export const fileVersionUploadState = fileVersionUploadStateSlice.reducer;
